import { FC, useContext, useEffect } from 'react';

import {
  ErrorEventType,
  getLocaleSetting,
  PaymentSection,
  PaymentSectionErrorEvent,
  t,
} from '@onlinepayments/payments-library';
import { captureException } from '@sentry/browser';

import PriceComponent from '../../components/PriceComponent';
import ProductListContainer from '../../components/ProductList';
import { AmountType } from '../../enums/AmountType';
import { OrderPaymentStatus } from '../../enums/OrderPaymentStatus';
import { SentryTag } from '../../enums/SentryTag';
import IOrderDetails from '../../interfaces/OrderDetails';
import { OrderDetailsContext } from '../../OrderDetailsContext';
import { PayorderContext } from '../../PayorderContext';
import * as payorderApi from '../../services/payorderApi';
import { getAciActionUrl } from '../../utils/copyAndPay';
import { ofAmountType } from '../../utils/misc';

import { SaferHomesCheckbox } from './components/SaferHomesCheckbox';
import { filterSaferHomesProducts, loadSaferHomesProductList, mapSaferHomeNames } from './utils';

interface IPaymentContainerProps {
  goToPaymentProcessingPage: () => void;
  orderDetails: IOrderDetails;
}

const PaymentContainer: FC<IPaymentContainerProps> = ({
  orderDetails,
  goToPaymentProcessingPage,
}) => {
  const {
    contextState: {
      paymentContext: { contextId: paymentContextId },
      payorderContext: { contextId: payorderContextId },
    },
    dispatchContext,
  } = useContext(PayorderContext);

  const { orderItems, saferHomesState, setSaferHomesState } = useContext(OrderDetailsContext);

  const {
    payment: { showProductList, requireSaferHomesAcknowledgement },
  } = getLocaleSetting();
  const additionalSubmitCondition = saferHomesState.saferHomesText ? SaferHomesCheckbox : undefined;

  useEffect(() => {
    if (!orderItems) {
      setSaferHomesState({ isLoaded: false, saferHomesText: undefined });
      return;
    }

    if (!requireSaferHomesAcknowledgement || orderItems.length === 0) {
      setSaferHomesState({ isLoaded: true, saferHomesText: undefined });
      return;
    }

    const loadSaferHomes = async () => {
      try {
        const saferHomesProductList = await loadSaferHomesProductList();
        const matchedProducts = filterSaferHomesProducts(orderItems, saferHomesProductList);

        if (matchedProducts.length === 0) {
          setSaferHomesState({ isLoaded: true, saferHomesText: undefined });
          return;
        }
        const productsNames = mapSaferHomeNames(matchedProducts);

        setSaferHomesState({
          isLoaded: true,
          saferHomesText: t('payment.safer_homes.text', undefined, {
            productsNames,
          }),
        });
      } catch (error) {
        captureException(error, (scope) => {
          scope.setTag(SentryTag.PaymentContextId, paymentContextId);
          scope.setTag(SentryTag.ErrorType, 'safer-homes');
          scope.setFingerprint(['fetch-errors']);
          return scope;
        });
        setSaferHomesState({ isLoaded: true, saferHomesText: undefined });
      }
    };

    loadSaferHomes();
  }, [orderItems, paymentContextId, requireSaferHomesAcknowledgement, setSaferHomesState]);

  function handleAciError() {
    dispatchContext({ type: 'RemovePaymentContextId' });
  }

  return (
    <div className="PaymentContainer" data-testid="PaymentContainer">
      <div className="w-full">
        <h2 className="my-150">{t('payment.payment_options_header')}</h2>
        {saferHomesState.isLoaded && (
          <PaymentSection
            redirectUrl={getAciActionUrl()}
            onSuccess={goToPaymentProcessingPage}
            onBeforeSubmitValidation={async () => {
              if (payorderContextId) {
                let orderDetailsResponse: IOrderDetails | undefined;

                try {
                  orderDetailsResponse = await payorderApi.getOrderDetails(payorderContextId);
                } catch (ignore) {
                  /* empty */
                }

                if (orderDetailsResponse?.paymentStatus === OrderPaymentStatus.Paid) {
                  throw new Error(t('order.already_paid_notification'));
                }
              }
            }}
            onError={(error: PaymentSectionErrorEvent) => {
              if (error.type === ErrorEventType.AciError) {
                handleAciError();
              }
            }}
            orderInfo={{
              firstName: orderDetails.billingContact.firstName || undefined,
              lastName: orderDetails.billingContact.lastName || undefined,
              businessName: orderDetails.billingContact.businessInfo?.businessName || undefined,
              totalAmount: orderDetails?.orderAmounts?.find(ofAmountType(AmountType.Total))
                ?.grossAmount,
            }}
            additionalSubmitCondition={additionalSubmitCondition}
            orderDetails={
              <>
                {showProductList && <ProductListContainer />}
                <div className="my-100">
                  <PriceComponent
                    orderAmounts={orderDetails.orderAmounts}
                    amountLeftToPay={0}
                    orderCurrency={orderDetails.currency}
                  />
                </div>
              </>
            }
          />
        )}
      </div>
    </div>
  );
};

export default PaymentContainer;
